import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';

import { ErrorFallback } from '@dfq/shared/components/ErrorFallback';
import {
  installClientErrorReporting,
  reportCrashMarkerIfPresent,
  reportIfNew,
} from '@dfq/shared/lib/installClientErrorReporting';
import { Toaster } from '@dfq/shared/components/ui/sonner';
import { store } from '@dfq/shared/stores/index';

import { AdminRouterProvider } from '@/router';

import './App.css';

/**
 * Singleton QueryClient for the Console SPA.
 *
 * Defaults:
 *  - staleTime: 30s — conservative; avoids background refetches on every
 *    focus event for admin data that doesn't change sub-minute.
 *  - retry: 1 — one automatic retry on transient errors; avoids hammering
 *    the API on systemic failures.
 *
 * Individual queries may override these via their own `queryOptions`.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Installed at module scope rather than in an effect: a crash during the
// very first render happens before any effect runs, and that is exactly the
// failure worth capturing.
installClientErrorReporting('console');
// Reports a crash marker left by the previous load (SH-CONTOUR-107) before
// anything else runs.
reportCrashMarkerIfPresent('console');

function App() {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => reportIfNew('console', error, 'render', info.componentStack ?? undefined)}
    >
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          {/* CON-INFRA-001 — mounts `createBrowserRouter` for all /admin/* routes. */}
          <AdminRouterProvider />
          {/* SH-INFRA-006 — single global <Toaster> at the root. */}
          <Toaster />
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
